import { createId } from './ids';
import { findLayerById } from './layerTree';
import {
  CHANNEL_OVERLAY_COLOR,
  type AlphaChannel,
  type ChannelRef,
  type ColorChannelVisibility,
  type DocumentMeta,
  type RGBA,
} from './types';

/** Id of the one Quick Mask channel a document can hold. */
export const QUICK_MASK_ID = 'quickmask';

export const COLOR_CHANNEL_NAMES = ['Red', 'Green', 'Blue'] as const;

export function createAlphaChannel(name: string): AlphaChannel {
  return {
    id: createId('channel'),
    name,
    kind: 'alpha',
    visible: false,
    color: { ...CHANNEL_OVERLAY_COLOR },
  };
}

export function createQuickMaskChannel(): AlphaChannel {
  return {
    id: QUICK_MASK_ID,
    name: 'Quick Mask',
    kind: 'quickMask',
    visible: true,
    color: { ...CHANNEL_OVERLAY_COLOR },
  };
}

/** `Alpha 1`, `Alpha 2`, … skipping names already in the document. */
export function nextChannelName(channels: readonly AlphaChannel[], base = 'Alpha'): string {
  const taken = new Set(channels.map((channel) => channel.name));
  for (let index = 1; index < 1000; index += 1) {
    const name = `${base} ${index}`;
    if (!taken.has(name)) return name;
  }
  return base;
}

export function findChannel(doc: DocumentMeta, id: string | null): AlphaChannel | null {
  if (!id) return null;
  return doc.channels.find((channel) => channel.id === id) ?? null;
}

export function quickMaskChannel(doc: DocumentMeta): AlphaChannel | null {
  return doc.channels.find((channel) => channel.kind === 'quickMask') ?? null;
}

export function channelRefEquals(a: ChannelRef, b: ChannelRef): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'color' && b.kind === 'color') return a.index === b.index;
  if (a.kind === 'mask' && b.kind === 'mask') return a.layerId === b.layerId;
  if (a.kind === 'alpha' && b.kind === 'alpha') return a.id === b.id;
  return true;
}

/**
 * A reference is only usable while the thing it points at exists: deleting a
 * layer or a channel has to fall back to the composite rather than leave the
 * panel pointing at nothing.
 */
export function channelRefExists(doc: DocumentMeta, ref: ChannelRef): boolean {
  if (ref.kind === 'alpha') return findChannel(doc, ref.id) !== null;
  if (ref.kind === 'mask') {
    const layer = findLayerById(doc.layers, ref.layerId);
    return Boolean(layer?.hasMask);
  }
  return true;
}

export function colorChannelCount(visible: ColorChannelVisibility): number {
  return Number(visible.r) + Number(visible.g) + Number(visible.b);
}

export function visibleAlphaChannels(doc: DocumentMeta): AlphaChannel[] {
  return doc.channels.filter((channel) => channel.visible);
}

/** How the active layer's mask is presented on the canvas. */
export type MaskViewMode = 'off' | 'grey' | 'overlay';

/** The mask a mask view refers to: the selected one, else the active layer's. */
export function maskViewLayerId(doc: DocumentMeta, active: ChannelRef): string | null {
  const layerId = active.kind === 'mask' ? active.layerId : doc.activeLayerId;
  const layer = findLayerById(doc.layers, layerId);
  return layer?.hasMask ? layer.id : null;
}

/**
 * What the canvas has to draw for the current channel selection. Exactly one
 * `grey*` field can be set, and it wins over the colour channels: a single
 * visible channel is shown as greyscale, because that is the only way to judge
 * its values. Any other combination shows the colour channels with the
 * single-channel ones laid over them as a red mask.
 */
export interface ChannelViewOptions {
  /** Colour channel shown alone as grey, or -1. */
  greyColor: number;
  /** Alpha channel shown alone as grey. */
  greyChannel: string | null;
  /** Layer whose mask is shown alone as grey. */
  greyMask: string | null;
  colorVisible: ColorChannelVisibility;
  /** Alpha channels drawn over the colour ones. */
  overlays: string[];
  /** Layer whose mask is drawn over the colour ones. */
  overlayMask: string | null;
  overlayColor: RGBA;
}

/**
 * `null` means the plain composite, which is both the common case and the
 * cheapest path: no extra pass runs unless a channel is actually singled out.
 */
export function planChannelView(
  doc: DocumentMeta,
  maskView: MaskViewMode,
  maskLayerId: string | null,
): ChannelViewOptions | null {
  const alphas = visibleAlphaChannels(doc);
  const colors = colorChannelCount(doc.colorVisible);
  const mask = maskView === 'overlay' ? maskLayerId : null;
  const total = colors + alphas.length + (mask ? 1 : 0);

  const base: ChannelViewOptions = {
    greyColor: -1,
    greyChannel: null,
    greyMask: null,
    colorVisible: doc.colorVisible,
    overlays: [],
    overlayMask: null,
    overlayColor: alphas[0]?.color ?? CHANNEL_OVERLAY_COLOR,
  };

  // Alt+click on a mask asks for the mask itself, whatever else is visible.
  if (maskView === 'grey' && maskLayerId) return { ...base, greyMask: maskLayerId };
  if (colors === 3 && alphas.length === 0 && !mask) return null;

  if (total === 1) {
    if (colors === 1) {
      return { ...base, greyColor: doc.colorVisible.r ? 0 : doc.colorVisible.g ? 1 : 2 };
    }
    if (alphas.length === 1) return { ...base, greyChannel: alphas[0].id };
    return { ...base, greyMask: mask };
  }

  return {
    ...base,
    overlays: alphas.map((channel) => channel.id),
    overlayMask: mask,
  };
}
